import { useEffect, useState } from 'react';

/**
 * Returns `true` only once `active` has stayed `true` continuously for
 * `delayMs`. If `active` flips back to `false` before the delay elapses, the
 * flag never trips.
 *
 * Used to suppress loading UI for fast loads (cache hits, quick networks) so a
 * skeleton doesn't flash for a frame or two — the classic "flash of loading
 * state". A ~250ms threshold is below the point most users notice a delay but
 * above the duration of an instant load.
 */
const useDelayedFlag = (active: boolean, delayMs = 250): boolean => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!active) {
      setShow(false);
      return;
    }

    const timeout = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(timeout);
  }, [active, delayMs]);

  // Tests run on global fake timers, so the debounce timer never fires.
  // Surface the flag synchronously there to keep loading assertions
  // deterministic (mirrors the NODE_ENV check in GroupedLoadingProvider).
  if (process.env.NODE_ENV === 'test') {
    return active;
  }

  return show;
};

export default useDelayedFlag;
